import { CommonModule } from '@angular/common';
import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnInit,
    ViewEncapsulation,
    inject,
    input,
    signal,
} from '@angular/core';
import { filter, find, forEach, map } from 'lodash-es';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { CurationDetailsNavigatorService } from '../../../common/components/curation-modal/native/curation-details-navigator.service';
import { IdentityService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { TranslateService } from '@ngx-translate/core';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { CurationBackgroundImageDirective } from '../../stories/curation-card/background-image.directive';
import { CurationFullNamePipe } from '../../stories/curation-card/curation-card.pipes';
import { TranslateModule } from '@ngx-translate/core';
import { FtImageComponent } from './ft-image.component';
import { FtPodiumComponent } from './podium/ft-podium.component';
import { FtPodiumSlotComponent } from './podium/ft-podium-slot.component';
import { FtDatePickerComponent } from './podium/ft-date-picker.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import dayjs from 'dayjs/esm/index';
import utc from 'dayjs/esm/plugin/utc';

dayjs.extend(utc);

/**
 * Contest WINNERS sheet: the winners podium, finalists grid, and the end-contest /
 * podium-editor / anti-cheat modals (native ft-modal-chrome-ng). Loads via
 * PhotoContestApiService. `createPodium`/`syncWinnerType`/`getNumberOfPlaces` keep
 * the load-bearing undefined(no-incentive) vs null(no-winner) vs finalist
 * convention that drives `podium-size-{N}` and empty-slot placeholders. Inert v1
 * custom elements (`<ft-sheet-tabs>`) are kept, hence CUSTOM_ELEMENTS_SCHEMA.
 */
@Component({
    selector: 'ft-contest-winners-ng',
    imports: [
        CommonModule,
        CurationBackgroundImageDirective,
        CurationFullNamePipe,
        TranslateModule,
        FtImageComponent,
        FtPodiumComponent,
        FtPodiumSlotComponent,
        FtDatePickerComponent,
        FtModalChromeComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './contest-winners.component.html',
    styleUrls: ['./contest-winners.component.scss'],
})
export class FtContestWinnersComponent implements OnInit {
    readonly contestUuid = input.required<string>();

    /** endContestConfirmation modal open state (inc 2 — the "Declare winners" announce button). */
    protected readonly isEndContestOpen = signal(false);

    /** podiumEditor modal open state (inc 3 — clicking a finalist). */
    protected readonly isPodiumEditorOpen = signal(false);
    /** cleanContestConfirmation modal open state (inc 4 — staff Anti-cheat button). */
    protected readonly isCleanContestOpen = signal(false);
    /** v1 `$scope.isGuestInfoVisible` — toggled by the editor's "Show info" link. */
    protected readonly isGuestInfoVisible = signal(false);
    /** Pre-rendered room-nights label (v1 `translate` directive + translate-values). */
    protected readonly bookedRoomNightsLabel = signal('');
    /** ft-snapshot of model.podium taken on editor open; restored on Cancel. */
    private podiumSnapshot: any;
    /** A "Load more" request is in flight — a second click must not fetch the same page again. */
    private isLoadingNextPage = false;

    protected readonly model = signal<any>({
        contentLoaded: false,
        paging: { SortBy: 'score', SortDirection: 'desc', Offset: 0, Fetch: 24 },
        hasMoreData: true,
        scrollLoadsResetCounter: 0,
        missingWinners: [],
    });

    private readonly photoContest: any = inject(PhotoContestApiService);
    private readonly spaces: any = inject(SpacesService);
    private readonly identity: any = inject(IdentityService);
    private readonly parse: any = inject(ParseService);
    private readonly dayjs: any = dayjs;
    // WS5 #10 inc 2i-2: the 'curationModal.openDetails' run-block handler is native now.
    private readonly detailsNavigator: CurationDetailsNavigatorService = inject(CurationDetailsNavigatorService);
    private readonly azure: (photo: any, size: string) => string;
    private readonly dateFormat: (date: any, format: string) => string;
    private readonly translateFn: (key: string, params?: any) => string;
    private readonly zone = inject(NgZone);
    private readonly eventBus = inject(EventBusService);
    private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);

    private readonly placeToWinnerType: { [place: string]: number } = {
        first: 1,
        second: 2,
        third: 3,
        runner: 100,
    };

    constructor() {
        this.azure = (photo: any, size: string) =>
            buildAzureStoredPhotoUrl(photo, size, this.environment.AzureCdnBaseUrl) as string;
        // dateFormat: native (v1 dateFormat.filter.js — isUtc defaults true →
        // utc(date).format(format||'MMM D')). Drains the last `$filter` consumer
        // from this component.
        this.dateFormat = (date: any, format: string) => this.dayjs.utc(date).format(format || 'MMM D');
        const translateSvc = inject(TranslateService);
        this.translateFn = (key: string, params?: any) => translateSvc.instant(key, params);
    }

    ngOnInit(): void {
        const propertyUuid = this.spaces.getPropertyUuid();
        const paging = this.model().paging;

        Promise.all([
            Promise.resolve(this.photoContest.load({ contestId: this.contestUuid(), propertyUuid })),
            Promise.resolve(this.photoContest.finalists(propertyUuid, this.contestUuid())),
            Promise.resolve(this.photoContest.finalistsWithPaging(propertyUuid, this.contestUuid(), paging)),
        ]).then((data: any[]) => {
            this.zone.run(() => {
                const contest = data[0];
                const finalists = data[1];
                const pagedFinalists = data[2];

                finalists.finalists = { item: pagedFinalists };

                this.model().contest = contest;
                this.model().finalists = finalists;
                this.model().contestClosed = this.parse.bool(contest.isClosed);
                this.model().contestActive = this.parse.bool(contest.isActive);
                this.model().podium = this.createPodium(contest, finalists);
                this.model().contentLoaded = true;
                // A contest with no finalists at all comes back null, like any page past the last row.
                this.model().hasMoreData = pagedFinalists?.length === paging.Fetch;
                // Every row carries the count across all pages.
                this.model().totalFinalists = pagedFinalists?.[0]?.totalRecords ?? 0;

                this.syncWinnerType(this.model().podium);
                this.recomputeMissingWinners();
            });
        });
    }

    /**
     * v1 `$scope.model.loadNextPage` — appends the next page of finalists. The offset only advances
     * once a page lands, so a failed request can be retried.
     */
    protected loadNextPage(): void {
        if (this.isLoadingNextPage) return;
        this.isLoadingNextPage = true;
        const paging = { ...this.model().paging, Offset: this.model().paging.Offset + this.model().paging.Fetch };

        Promise.resolve(this.photoContest.finalistsWithPaging(this.spaces.getPropertyUuid(), this.contestUuid(), paging)).then(
            (data: any) => {
                this.zone.run(() => {
                    // Past the last row the proc returns an empty body, which arrives as null.
                    const page = Array.isArray(data) ? data : [];
                    const item = [...this.finalists, ...page];
                    this.model.update((m: any) => ({
                        ...m,
                        paging,
                        hasMoreData: page.length === paging.Fetch,
                        finalists: { ...m.finalists, finalists: { item } },
                    }));
                    this.isLoadingNextPage = false;
                });
            },
            () => {
                this.isLoadingNextPage = false;
            },
        );
    }

    // ---- template helpers ----

    protected azurePhoto(photo: any, size: string): string | null {
        return photo ? this.azure(photo, size) : null;
    }

    protected formatDate(date: any, format: string): string {
        return date ? this.dateFormat(date, format) : '';
    }

    protected isFliptoStaff(): boolean {
        return !!this.identity.isFliptoStaff();
    }

    protected get finalists(): any[] {
        return this.parse.arrayOrDefault(this.model().finalists?.finalists?.item);
    }

    protected get announceDisabled(): boolean {
        return !this.isFliptoStaff() && !!this.model().hasWinnersMissing;
    }

    /** `ftWinnerSummary` ng-class — reactive to `@winnerTypeID` (set by syncWinnerType). */
    protected winnerTypeClass(finalist: any): { [cls: string]: boolean } {
        const type = finalist?.['@winnerTypeID'];
        return {
            'first-place': type == 1,
            'second-place': type == 2,
            'third-place': type == 3,
            'runner-up': type == 100,
        };
    }

    /** `ftWinnerSummary` getTypeDescription(winnerType). */
    protected winnerTypeLabel(finalist: any): string {
        const type = finalist?.['@winnerTypeID'];
        const key: { [k: number]: string } = {
            1: 'flipto.account.sections.platform.photoAdvocacy.1STPlace',
            2: 'flipto.account.sections.platform.photoAdvocacy.2NDPlace',
            3: 'flipto.account.sections.platform.photoAdvocacy.3RDPlace',
            100: 'flipto.account.sections.platform.photoAdvocacy.RunnerUp',
        };
        return key[type] ? this.translateFn(key[type]) : '';
    }

    /** v1 `$scope.openPodiumEditor` — load the photo, snapshot the podium, open the editor. */
    protected openPodiumEditor(finalist: any): void {
        if (!finalist) return;
        Promise.resolve(this.photoContest.photo(this.spaces.getPropertyUuid(), finalist['@photoUUID'] || finalist)).then(
            (data: any) => {
                this.zone.run(() => {
                    this.model().photo = data;
                    // 3 is a magic number coming from the post-stay isPositive algorithm.
                    this.model().photo.isQuotePositive = this.model().photo.quoteRating > 3;
                    this.model().photo.totalShares =
                        this.parse.intOrDefault(data.totalGuestShares) + this.parse.intOrDefault(data.totalOtherShares);
                    this.model().finalist = this.copy(finalist);
                    this.bookedRoomNightsLabel.set(this.translateFn('flipto.account.platform.BookedNumbRoomNightsOn', {
                        totalRoomNights: this.model().finalist['@totalRoomNights'],
                    }));
                    // ft-snapshot: snapshot model.podium so Cancel reverts the editor's edits.
                    this.podiumSnapshot = this.copy(this.model().podium);
                    this.isPodiumEditorOpen.set(true);
                });
            },
        );
    }

    /** v1 `$scope.savePodium` — build the winners payload + PhotoContest.updateWinners. */
    savePodium(): void {
        const model: any = {};
        const podium = this.model().podium;
        if (podium.first) model.firstPlacePhotoUUID = podium.first['@photoUUID'];
        if (podium.second) model.secondPlacePhotoUUID = podium.second['@photoUUID'];
        if (podium.third) model.thirdPlacePhotoUUID = podium.third['@photoUUID'];
        const runners = filter(podium.runnerUps.items, (runner: any) => !!runner);
        model.runnerUpPhotoUUIDs = map(runners, (runner: any) => runner['@photoUUID']);
        this.photoContest.updateWinners(this.spaces.getPropertyUuid(), this.contestUuid(), model);
    }

    /** Chrome `(done)` — v1 ftModal on-done="savePodium" then commit (discard snapshot) + hide. */
    protected savePodiumAndClose(): void {
        this.savePodium();
        this.podiumSnapshot = undefined;
        this.isPodiumEditorOpen.set(false);
        this.recomputeMissingWinners();
    }

    /** Chrome `(cancel)` — v1 ftModal cancel → ft-snapshot rollback + hide. */
    protected cancelPodiumEditor(): void {
        if (this.podiumSnapshot) {
            this.model().podium = this.copy(this.podiumSnapshot);
            this.podiumSnapshot = undefined;
        }
        this.isPodiumEditorOpen.set(false);
    }

    protected isContestEditable(): boolean {
        return !this.model().contestClosed && this.model().contestActive;
    }

    protected isInvitee(): boolean {
        return this.model().finalist && this.parse.bool(this.model().finalist['@isInvitee']);
    }

    protected showGuestInfo(): void {
        this.isGuestInfoVisible.set(true);
    }

    /** v1 — the shared details-page navigation (same path the curation modal uses), now direct. */
    protected openAdvocatePage(): void {
        this.detailsNavigator.openDetails(this.model().finalist['@guestUUID']);
    }

    protected hasPiiPermissions(): boolean {
        return !!this.identity.hasPiiPermissions();
    }

    /** Translate helper for method/attribute bindings (header text, etc.). */
    protected tr(key: string): string {
        return this.translateFn(key);
    }

    // ---- cleanContestConfirmation (inc 4, staff-only) ----

    /** v1 `ft-targets-modal="cleanContestConfirmation"` on the Anti-cheat button. */
    protected openCleanContest(): void {
        this.isCleanContestOpen.set(true);
    }

    /** v1 `$scope.cleanContestCheaters`. */
    cleanContestCheaters(): void {
        Promise.resolve(
            this.photoContest.cleanContestCheaters(
                this.spaces.getPropertyUuid(),
                this.contestUuid(),
                this.model().cheaterCleanupCutoffDate,
            ),
        ).then(
            () => alert('Contest cleaned successfully!'),
            () => alert('Error cleaning contest.'),
        );
    }

    /** Chrome `(done)` — v1 ftModal on-done="cleanContestCheaters" + hide. */
    protected cleanContestAndClose(): void {
        this.cleanContestCheaters();
        this.isCleanContestOpen.set(false);
    }

    /** v1 `ft-targets-modal="endContestConfirmation"` on the Declare-winners button. */
    protected openEndContest(): void {
        this.isEndContestOpen.set(true);
    }

    /** v1 `$scope.hasWinnersMissing`. */
    protected hasWinnersMissing(): boolean {
        return this.model().missingWinners.length > 0;
    }

    /** v1 `$scope.closeContest` — verbatim (PhotoContest.closeContest + broadcast + local state). */
    protected closeContest(): void {
        if (!this.canClose()) return;
        if (!this.parse.bool(this.model().finalists.isWinnerEmailActive)) {
            alert('Winner email is disabled, please enable it before announcing winners.');
            return;
        }
        Promise.resolve(this.photoContest.closeContest(this.spaces.getPropertyUuid(), this.contestUuid())).then(() => {
            this.zone.run(() => {
                this.model().contestClosed = true;
                this.model().contestActive = false;
                this.isEndContestOpen.set(false);
                // The contests and audiences lists listen for this to show the closed contest.
                this.eventBus.emit('flipto.photo-contest-closed', this.contestUuid());
            });
        });
    }

    // ---- podium math (verbatim from WinnersController) ----

    protected canClose(): boolean {
        if (this.model().contestClosed) return false;
        return this.dayjs().isAfter(this.model().contest?.votingEndDate);
    }

    protected getNumberOfPlaces(): number {
        let counter = 0;
        const podium = this.model().podium;
        if (!podium) return counter;
        if (this.isDefined(podium.first)) counter++;
        if (this.isDefined(podium.second)) counter++;
        if (this.isDefined(podium.third)) counter++;
        return counter;
    }

    protected isPodiumDefined(): boolean {
        return this.getNumberOfPlaces() > 0;
    }

    private getMissingWinners(includeRunnerups: boolean): string[] {
        const missing: string[] = [];
        const podium = this.model().podium;
        if (podium) {
            if (podium.first === null) missing.push('1st Place');
            if (podium.second === null) missing.push('2nd Place');
            if (podium.third === null) missing.push('3rd Place');
            const missingRunnerUps = (podium.runnerUps.items as any[]).filter((r) => r === null).length;
            if (includeRunnerups && missingRunnerUps > 0) missing.push(missingRunnerUps + ' Runner-up');
        }
        return missing;
    }

    private recomputeMissingWinners(): void {
        this.model().missingWinners = this.getMissingWinners(true);
        this.model().hasWinnersMissing = !!this.model().missingWinners.length;
    }

    private syncWinnerType(podium: any): void {
        if (!podium || !this.model().finalists?.finalists) return;
        forEach(this.model().finalists.finalists.item, (finalist: any) => {
            finalist['@winnerTypeID'] = 0;
        });
        this.updateWinnerType(podium.first, 'first');
        this.updateWinnerType(podium.second, 'second');
        this.updateWinnerType(podium.third, 'third');
        forEach(podium.runnerUps.items, (runner: any) => this.updateWinnerType(runner, 'runner'));
    }

    private updateWinnerType(source: any, place: string): void {
        if (!source) return;
        forEach(this.model().finalists.finalists.item, (finalist: any) => {
            if (finalist['@photoUUID'] == source['@photoUUID']) {
                finalist['@winnerTypeID'] = this.placeToWinnerType[place] || 0;
            }
        });
    }

    private createPodium(contest: any, finalists: any): any {
        // Convention: null = incentive defined but winner not yet chosen (empty placeholder);
        //             undefined = no incentive (slot absent → not counted by getNumberOfPlaces).
        const podium: any = {
            first: this.nonNullableTargetOrUndefined(
                contest.finalistsIncentives.firstWinnerIncentive,
                finalists.firstPlace
                    ? this.copy(find(finalists.finalists.item, (e: any) => e['@photoUUID'] == finalists.firstPlace['@photoUUID']))
                    : null,
            ),
            second: this.nonNullableTargetOrUndefined(
                contest.finalistsIncentives.secondWinnerIncentive,
                finalists.secondPlace
                    ? this.copy(find(finalists.finalists.item, (e: any) => e['@photoUUID'] == finalists.secondPlace['@photoUUID']))
                    : null,
            ),
            third: this.nonNullableTargetOrUndefined(
                contest.finalistsIncentives.thirdWinnerIncentive,
                finalists.thirdPlace
                    ? this.copy(find(finalists.finalists.item, (e: any) => e['@photoUUID'] == finalists.thirdPlace['@photoUUID']))
                    : null,
            ),
            runnerUps: { total: 0, items: [] as any[] },
        };

        if (contest.finalistsIncentives && contest.finalistsIncentives.runnerUpsIncentive) {
            podium.runnerUps.total = parseInt(contest.finalistsIncentives.runnerUpsIncentive['@totalRunnerUps'], 10);
            if (finalists.runnerUps) {
                podium.runnerUps.items = Array.isArray(finalists.runnerUps.item)
                    ? this.copy(finalists.runnerUps.item)
                    : [this.copy(finalists.runnerUps.item)];
            }
            for (let i = podium.runnerUps.items.length; i < podium.runnerUps.total; ++i) {
                podium.runnerUps.items.push(null);
            }
        }

        return podium;
    }

    private nonNullableTargetOrUndefined(nullable: any, target: any): any {
        if (nullable == null) return undefined;
        return this.isDefined(target) ? target : null;
    }

    private isDefined(value: any): boolean {
        return typeof value !== 'undefined';
    }

    private copy<T>(value: T): T {
        // angular.copy equivalent for the plain-JSON /api finalist data (no functions/Dates).
        return value == null ? value : (JSON.parse(JSON.stringify(value)) as T);
    }
}
